import { Download, HardDrive, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { t } from '@/shared/i18n/t';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatBytes } from '@/shared/lib/format-bytes';
import { Button } from '@/shared/ui/button/button';
import { Card } from '@/shared/ui/card/card';
import { CardContent } from '@/shared/ui/card/card-content';
import { CardDescription } from '@/shared/ui/card/card-description';
import { CardHeader } from '@/shared/ui/card/card-header';
import { CardTitle } from '@/shared/ui/card/card-title';
import { exportData } from '../api/export-data';
import { useStorageInfo } from '../hooks/use-storage-info';

export function DeviceCard() {
  const { info, files, refresh } = useStorageInfo();
  const { tn } = useTranslation();

  async function askPersistence() {
    const granted = await navigator.storage?.persist?.();
    if (granted) toast.success(t('Persistent storage is on'));
    else toast.info(t('The browser has not granted it yet'));
    await refresh();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('This device')}</CardTitle>
        <CardDescription>
          {t(
            'So the browser never erases the local copy when space runs low, ask for persistent storage. On iPhone, add the app to the home screen.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <HardDrive className="size-4 text-muted-foreground" />
            {info?.persisted ? t('Persistent storage is on') : t('Storage is not persistent')}
            {info?.usage != null && ` · ${t('{size} used', { size: formatBytes(info.usage) })}`}
          </span>
          {info && !info.persisted && (
            <Button variant="outline" size="sm" onClick={() => void askPersistence()}>
              {t('Ask for persistence')}
            </Button>
          )}
        </div>
        {files && (
          <span className="text-muted-foreground">
            {tn(
              files.count,
              '{count} file on this device ({size})',
              '{count} files on this device ({size})',
              {
                size: formatBytes(files.size),
              },
            )}
            {files.pending > 0 && ` · ${t('{count} to upload', { count: files.pending })}`}
          </span>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" onClick={() => void exportData()}>
            <Download /> {t('Export data (JSON)')}
          </Button>
          <Button variant="outline" nativeButton={false} render={<Link to="/trash" />}>
            <Trash2 /> {t('Trash')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
